import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import { Stars } from './Stars';
import { dateTime } from '../lib/format';
import type { AdminFeedbacksResponse, Feedback } from '../api/types';

const FILTROS: { key: string; label: string }[] = [
  { key: 'pendente', label: 'Aguardando conferência' },
  { key: 'aprovado', label: 'Publicados' },
  { key: 'recusado', label: 'Recusados' },
  { key: 'todos', label: 'Todos' },
];

const ROTULO_STATUS: Record<string, string> = {
  pendente: 'Em análise',
  aprovado: 'Publicado',
  recusado: 'Recusado',
};

/**
 * Moderação dos feedbacks.
 *
 * Abre na fila do que ainda não foi conferido: é o que segura avaliação boa
 * fora do mural. O que já foi decidido continua acessível para rever — inclusive
 * para despublicar algo aprovado por engano.
 */
export function FeedbackPanel() {
  const [data, setData] = useState<AdminFeedbacksResponse | null>(null);
  const [filtro, setFiltro] = useState('pendente');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [respondendo, setRespondendo] = useState<number | null>(null);
  const [resposta, setResposta] = useState('');

  const carregar = useCallback(async () => {
    try {
      setData(await api.adminFeedbacks({ status: filtro }));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os feedbacks.');
    }
  }, [filtro]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function agir(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      setOk(mensagem);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui concluir.');
    } finally {
      setOcupado(false);
    }
  }

  function moderar(f: Feedback, status: 'aprovado' | 'recusado' | 'pendente') {
    // O motivo é obrigatório na recusa e o cliente o lê na página dele — a API
    // recusa sem ele, então perguntamos antes de gastar a viagem.
    let motivo: string | undefined;
    if (status === 'recusado') {
      const informado = window.prompt('Motivo da recusa (o cliente vê esta mensagem):');
      if (!informado?.trim()) return;
      motivo = informado.trim();
    }
    void agir(
      () => api.adminModerarFeedback(f.id, status, motivo),
      status === 'aprovado'
        ? 'Feedback publicado no mural.'
        : status === 'recusado'
          ? 'Feedback recusado. O cliente vê o motivo e pode reenviar.'
          : 'Feedback devolvido para a fila.',
    );
  }

  if (erro && !data) return <Banner kind="error">{erro}</Banner>;
  if (!data) return <Spinner label="Carregando os feedbacks…" />;

  return (
    <div className="panel">
      <h2>Feedbacks dos clientes</h2>
      <p className="muted">
        Nada entra no mural sozinho. A nota é do cliente e não se edita; o que você decide é se o comentário vai ao
        ar — e pode responder publicamente abaixo dele.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <div className="stat-row">
        <span className={data.totals.pendentes ? 'warn' : ''}>
          <b>{data.totals.pendentes}</b> aguardando conferência
        </span>
        <span>
          <b>{data.totals.aprovados}</b> publicados
        </span>
        <span>
          <b>{data.totals.recusados}</b> recusados
        </span>
        <span>
          nota do mural:{' '}
          <b>{data.summary.average === null ? '—' : data.summary.average.toFixed(1)}</b>
          <span className="dim"> ({data.summary.total})</span>
        </span>
      </div>

      <div className="admin-tabs">
        {FILTROS.map((f) => (
          <button key={f.key} className={`tab ${filtro === f.key ? 'active' : ''}`} onClick={() => setFiltro(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {data.items.length === 0 ? (
        <p className="muted">Nenhum feedback com esse filtro.</p>
      ) : (
        <ul className="mural moderar">
          {data.items.map((f) => (
            <li key={f.id} className="mural-item">
              <div className="mural-cabeca">
                {f.avatarUrl && <img src={f.avatarUrl} alt="" />}
                <div>
                  <b>{f.personaName ?? 'Cliente'}</b>
                  <div className="dim">
                    {f.steamId} · {dateTime(f.createdAt)}
                  </div>
                </div>
                <Stars value={f.rating} size="sm" />
                <span className={`feedback-status ${f.status}`}>{ROTULO_STATUS[f.status] ?? f.status}</span>
              </div>

              {f.comment ? <p className="mural-texto">{f.comment}</p> : <p className="muted">Só a nota, sem comentário.</p>}

              {f.status === 'recusado' && f.rejectReason && (
                <p className="dim">Motivo informado ao cliente: {f.rejectReason}</p>
              )}

              {f.storeReply && respondendo !== f.id && (
                <div className="mural-resposta">
                  <b>Resposta da loja</b>
                  <p>{f.storeReply}</p>
                </div>
              )}

              {respondendo === f.id && (
                <div className="field">
                  <textarea
                    rows={3}
                    maxLength={1500}
                    placeholder="Resposta pública, exibida sob o comentário no mural."
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                  />
                  <div className="actions-row">
                    <button
                      className="btn primary small"
                      disabled={ocupado}
                      onClick={() =>
                        void agir(
                          () => api.adminResponderFeedback(f.id, resposta),
                          resposta.trim() ? 'Resposta publicada.' : 'Resposta removida.',
                        ).then(() => setRespondendo(null))
                      }
                    >
                      Salvar resposta
                    </button>
                    <button className="btn ghost small" onClick={() => setRespondendo(null)}>
                      Cancelar
                    </button>
                    <span className="dim">Salvar em branco remove a resposta.</span>
                  </div>
                </div>
              )}

              <div className="actions-row">
                {f.status !== 'aprovado' && (
                  <button className="btn primary small" disabled={ocupado} onClick={() => moderar(f, 'aprovado')}>
                    Publicar
                  </button>
                )}
                {f.status !== 'recusado' && (
                  <button className="btn ghost small" disabled={ocupado} onClick={() => moderar(f, 'recusado')}>
                    Recusar
                  </button>
                )}
                {f.status === 'aprovado' && (
                  <button className="btn ghost small" disabled={ocupado} onClick={() => moderar(f, 'pendente')}>
                    Tirar do mural
                  </button>
                )}
                {respondendo !== f.id && (
                  <button
                    className="btn ghost small"
                    onClick={() => {
                      setRespondendo(f.id);
                      setResposta(f.storeReply ?? '');
                    }}
                  >
                    {f.storeReply ? 'Editar resposta' : 'Responder'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
